import React from 'react';
import { ArrowRight, Phone, Star } from 'lucide-react';
import { Button } from './ui/button';
import Typewriter from './Typewriter';

const HeroSection = ({ onGetQuote }) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden grain-overlay">
      {/* Dark gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#03121c] via-[#072a36] to-[#0f3a4a]" />

      {/* Floating animated blobs */}
      <div
        className="blob"
        style={{
          width: 480,
          height: 480,
          top: '-120px',
          left: '-120px',
          background: 'radial-gradient(circle, #00B3B3 0%, transparent 65%)',
          animationDelay: '0s',
        }}
      />
      <div
        className="blob"
        style={{
          width: 380,
          height: 380,
          bottom: '-100px',
          right: '-100px',
          background: 'radial-gradient(circle, #14e3e3 0%, transparent 65%)',
          animationDelay: '-6s',
          opacity: 0.28,
        }}
      />
      <div
        className="blob"
        style={{
          width: 260,
          height: 260,
          top: '40%',
          right: '15%',
          background: 'radial-gradient(circle, #00B3B3 0%, transparent 65%)',
          animationDelay: '-12s',
          opacity: 0.2,
        }}
      />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto py-20">
        {/* Google Rating Badge */}
        <a
          href="https://share.google/udiGsgTuj8s9oci0B"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8 bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/15 transition"
          data-testid="hero-google-rating"
        >
          <div className="flex">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" />
            ))}
          </div>
          <span className="text-white text-sm font-semibold">5.0</span>
          <span className="text-gray-300 text-xs">on Google Reviews</span>
        </a>

        {/* Logo */}
        <div className="mb-6 animate-fade-in flex flex-col items-center">
          <img
            src="https://customer-assets.emergentagent.com/job_phone-fix-quotes/artifacts/vsjgmfo7_NEXERA-02.png"
            alt="NEXERA"
            className="w-auto h-28 md:h-36"
            style={{ filter: 'drop-shadow(0 0 40px rgba(0, 179, 179, 0.45))' }}
          />
        </div>

        {/* Tagline */}
        <div className="mb-6 space-y-2">
          <p className="text-gray-200 text-lg md:text-xl">Fast. Quality. Reliable.</p>
          <h2
            className="text-3xl md:text-4xl font-bold"
            style={{ color: '#00B3B3' }}
            data-testid="hero-headline"
          >
            The Next Era of Repair
          </h2>
        </div>

        {/* Typewriter sub-headline */}
        <div className="mb-10 min-h-[3.5rem] flex items-center justify-center">
          <p className="text-white text-xl md:text-2xl font-light">
            <span className="text-gray-300 mr-2">We fix</span>
            <Typewriter
              className="font-semibold"
              words={[
                'iPhone Screens',
                'Samsung Batteries',
                'Cracked iPads',
                'Slow Laptops',
                'Water-Damaged Phones',
                'Gaming Consoles',
              ]}
            />
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            onClick={onGetQuote}
            className="text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg pulse-ring"
            style={{
              backgroundColor: '#00B3B3',
              boxShadow: '0 4px 14px 0 rgba(0, 179, 179, 0.39)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#009999')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#00B3B3')}
            size="lg"
            data-testid="hero-get-quote-btn"
          >
            Get a Repair Quote
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <Button
            variant="outline"
            className="px-8 py-6 text-lg rounded-lg transition-all duration-300 bg-white/5 backdrop-blur-sm"
            style={{ borderColor: '#00B3B3', borderWidth: '2px', color: '#00B3B3' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 179, 179, 0.15)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)')}
            size="lg"
            asChild
          >
            <a href="tel:2244199922" data-testid="hero-call-btn">
              <Phone className="mr-2 h-5 w-5" />
              Call Us: (224) 419-9922
            </a>
          </Button>
        </div>

        {/* Quick trust strip */}
        <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-gray-300 text-sm">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Same-Day Repair
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            90-Day Warranty
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-yellow-400" />
            OEM Certified
          </span>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-[2]" />
    </section>
  );
};

export default HeroSection;
